import { Database, ref, push, set, get, query, orderByChild, equalTo, remove, update } from 'firebase/database';
import { TeamsPhotoPost, TeamsPhotoComment } from '@fitzaa/shared';

export class WebTeamsPhotoFirebaseService {
    private database: Database | null = null;
    private databasePath: string = 'dev/teamsPhotos';

    public setDatabase(database: Database): void {
        this.database = database;
    }

    public setDatabasePath(path: string): void {
        this.databasePath = path;
    }

    async savePhotoPost(photoPost: TeamsPhotoPost): Promise<string> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, this.databasePath);
            const newPhotoRef = push(photoRef);

            // Create a clean photo post object without circular references
            const cleanPhotoPost = {
                challengeId: photoPost.challengeId,
                userId: photoPost.userId,
                userDisplayName: photoPost.userDisplayName,
                photoUrl: photoPost.photoUrl,
                teamsMessageId: photoPost.teamsMessageId,
                teamsChannelId: photoPost.teamsChannelId,
                caption: photoPost.caption === undefined ? null : photoPost.caption,
                postedAt: photoPost.postedAt,
                likes: photoPost.likes || 0,
                comments: photoPost.comments || [],
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                // Only include optional fields if they have values
                ...(photoPost.blobName && { blobName: photoPost.blobName }),
                ...(photoPost.thumbnailUrl && { thumbnailUrl: photoPost.thumbnailUrl }),
                ...(photoPost.thumbnailBlobName && { thumbnailBlobName: photoPost.thumbnailBlobName }),
                ...(photoPost.oneDriveFileId && { oneDriveFileId: photoPost.oneDriveFileId }),
                ...(photoPost.oneDriveDriveId && { oneDriveDriveId: photoPost.oneDriveDriveId }),
                ...(photoPost.oneDriveItemId && { oneDriveItemId: photoPost.oneDriveItemId }),
                // Include encryption metadata fields (always include if they exist, even if empty)
                ...(photoPost.encryptionKey !== undefined && { encryptionKey: photoPost.encryptionKey }),
                ...(photoPost.encryptionAlgorithm !== undefined && { encryptionAlgorithm: photoPost.encryptionAlgorithm }),
                ...(photoPost.ivLength !== undefined && { ivLength: photoPost.ivLength }),
                ...(photoPost.originalContentType !== undefined && { originalContentType: photoPost.originalContentType }),
                ...(photoPost.originalSize !== undefined && { originalSize: photoPost.originalSize }),
                ...(photoPost.optimizedSize !== undefined && { optimizedSize: photoPost.optimizedSize }),
                ...(photoPost.compressionRatio !== undefined && { compressionRatio: photoPost.compressionRatio })
            };

            await set(newPhotoRef, cleanPhotoPost);
            return newPhotoRef.key || photoPost.id || '';
        } catch (error) {
            console.error('Error saving photo post to Firebase:', error);
            throw new Error(`Failed to save photo post: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    async deletePhoto(photoId: string): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            await remove(photoRef);
            console.log(`Photo post ${photoId} deleted from Firebase successfully`);
        } catch (error) {
            console.error('Error deleting photo post from Firebase:', error);
            throw new Error(`Failed to delete photo post: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    async getChallengePhotos(challengeId: string): Promise<TeamsPhotoPost[]> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photosRef = ref(this.database, this.databasePath);
            const challengePhotosQuery = query(
                photosRef,
                orderByChild('challengeId'),
                equalTo(challengeId)
            );

            const snapshot = await get(challengePhotosQuery);
            const photos: TeamsPhotoPost[] = [];

            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot) => {
                    const photoData = childSnapshot.val();
                    photos.push({
                        ...photoData,
                        id: childSnapshot.key || photoData.id
                    });
                });
            }

            // Sort by posted date (newest first)
            return photos.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
        } catch (error) {
            console.error('Error getting challenge photos from Firebase:', error);
            throw new Error(`Failed to get challenge photos: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    async updatePhotoLikes(photoId: string, newLikeCount: number): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            await update(photoRef, {
                likes: newLikeCount,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error updating photo likes in Firebase:', error);
            throw new Error(`Failed to update likes: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    async addCommentToPhoto(photoId: string, comment: TeamsPhotoComment): Promise<void> {
        if (!this.database) {
            throw new Error('Firebase database not initialized. Call setDatabase() first.');
        }

        try {
            const photoRef = ref(this.database, `${this.databasePath}/${photoId}`);
            const snapshot = await get(photoRef);

            if (!snapshot.exists()) {
                throw new Error('Photo not found');
            }

            const photoData = snapshot.val();
            const updatedComments = [...(photoData.comments || []), comment];

            await update(photoRef, {
                comments: updatedComments,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error adding comment to photo in Firebase:', error);
            throw new Error(`Failed to add comment: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
}
